// src/screens/OrdersCoverageScreen.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import request from "../utils/request";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ---------------- helpers ---------------- */

const num = (v) => {
  const n = typeof v === "string" ? Number(v.trim()) : Number(v);
  return Number.isFinite(n) ? n : null;
};

const coerceLatLngFromAny = (obj) => {
  if (!obj) return null;

  // direct lat/lng
  const latA = num(obj.lat), lngA = num(obj.lng);
  if (Number.isFinite(latA) && Number.isFinite(lngA)) return { lat: latA, lng: lngA };

  // latitude/longitude
  const latB = num(obj.latitude), lngB = num(obj.longitude);
  if (Number.isFinite(latB) && Number.isFinite(lngB)) return { lat: latB, lng: lngB };

  // nested location { coordinates: [lng,lat] }
  const c = obj.location || obj.coords || obj.geo;
  if (c) {
    const nested = coerceLatLngFromAny(c);
    if (nested) return nested;
    if (Array.isArray(c.coordinates) && c.coordinates.length >= 2) {
      const lng = num(c.coordinates[0]);
      const lat = num(c.coordinates[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
  }
  if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
    const lng = num(obj.coordinates[0]);
    const lat = num(obj.coordinates[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  return null;
};

// Try to read state from common shapes (prefer 2-letter)
// Accept full names and map them to USPS codes
const STATE_ABBR = {
  AL:"Alabama", AK:"Alaska", AZ:"Arizona", AR:"Arkansas", CA:"California", CO:"Colorado", CT:"Connecticut", DE:"Delaware",
  FL:"Florida", GA:"Georgia", HI:"Hawaii", ID:"Idaho", IL:"Illinois", IN:"Indiana", IA:"Iowa", KS:"Kansas", KY:"Kentucky",
  LA:"Louisiana", ME:"Maine", MD:"Maryland", MA:"Massachusetts", MI:"Michigan", MN:"Minnesota", MS:"Mississippi", MO:"Missouri",
  MT:"Montana", NE:"Nebraska", NV:"Nevada", NH:"New Hampshire", NJ:"New Jersey", NM:"New Mexico", NY:"New York",
  NC:"North Carolina", ND:"North Dakota", OH:"Ohio", OK:"Oklahoma", OR:"Oregon", PA:"Pennsylvania", RI:"Rhode Island",
  SC:"South Carolina", SD:"South Dakota", TN:"Tennessee", TX:"Texas", UT:"Utah", VT:"Vermont", VA:"Virginia",
  WA:"Washington", WV:"West Virginia", WI:"Wisconsin", WY:"Wyoming", DC:"District of Columbia",
};
const NAME_TO_ABBR = Object.fromEntries(Object.entries(STATE_ABBR).map(([abbr, name]) => [name.toLowerCase(), abbr]));

const coerceStateCode = (maybe) => {
  if (!maybe || typeof maybe !== "string") return null;
  const raw = maybe.trim();
  if (!raw) return null;
  // already 2-letter?
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const abbr = NAME_TO_ABBR[raw.toLowerCase()];
  return abbr || null;
};

// deterministic color per state
const PALETTE = ["#2563eb","#16a34a","#dc2626","#9333ea","#f59e0b","#0ea5e9","#ef4444","#22c55e","#f43f5e","#a855f7","#10b981","#eab308","#fb7185","#3b82f6","#14b8a6","#f97316"];
const hashIndex = (s) => { let h = 0; const str = String(s || ""); for (let i=0;i<str.length;i++) h = (h*31 + str.charCodeAt(i))>>>0; return h % PALETTE.length; };
const colorForState = (st) => PALETTE[hashIndex(st || "??")];

/* Extract lat/lng and state from an order */
const extractPoint = (o) => {
  // lat/lng (prefer explicit dropoff on order; fallback property shapes)
  const lat1 = num(o?.dropoffLat), lng1 = num(o?.dropoffLng);
  let coord = (Number.isFinite(lat1) && Number.isFinite(lng1)) ? { lat: lat1, lng: lng1 } : null;

  // embedded property/address fallbacks
  if (!coord && o?.property?.address) coord = coerceLatLngFromAny(o.property.address);
  if (!coord && o?.property) coord = coerceLatLngFromAny(o.property);
  if (!coord && o?.destination) coord = coerceLatLngFromAny(o.destination);

  // state
  const st =
    coerceStateCode(o?.property?.address?.state) ||
    coerceStateCode(o?.property?.state) ||
    coerceStateCode(o?.state) ||
    null;

  if (!coord) return null;
  return {
    lat: coord.lat,
    lng: coord.lng,
    state: st,
    id: String(o._id || o.id || Math.random()),
    label:
      (o?.property?.address?.line1 || o?.property?.name || o?.uniqueId || "Property"),
  };
};

/* Fit-to-points control */
function FitAllControl({ points }) {
  const map = useMap();
  const fitAll = useCallback(() => {
    const pts = points
      .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .map((p) => [p.lat, p.lng]);
    if (pts.length >= 2) map.fitBounds(L.latLngBounds(pts), { padding: [44,44] });
    else if (pts.length === 1) map.setView(pts[0], 6);
  }, [map, points]);
  // auto-fit on first load
  const did = useRef(false);
  useEffect(() => {
    if (!did.current) { did.current = true; fitAll(); }
  }, [fitAll]);

  return (
    <div
      onClick={fitAll}
      className="absolute top-3 right-3 z-[600] cursor-pointer select-none px-3 py-1.5 rounded-lg shadow bg-white/90 border border-[#e5e7eb] text-xs font-medium"
      title="Fit all points"
    >
      Fit All
    </div>
  );
}

/* ---------------- screen ---------------- */

export default function OrdersCoverageScreen() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heatSize, setHeatSize] = useState(true); // scale dot size by state volume

  // fetch completed orders (adjust limit if your API supports pagination)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data = [] } = await request.get("/orders", {
          params: { status: "completed", limit: 5000 },
        });
        if (!cancel) setOrders(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load completed orders:", e);
        if (!cancel) setOrders([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // build points
  const points = useMemo(() => {
    const arr = [];
    for (const o of orders) {
      const p = extractPoint(o);
      if (p && Number.isFinite(p.lat) && Number.isFinite(p.lng)) arr.push(p);
    }
    return arr;
  }, [orders]);

  // state counts
  const countsByState = useMemo(() => {
    const m = new Map();
    for (const p of points) {
      const k = p.state || "UNKNOWN";
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [points]);

  const summary = useMemo(() => {
    let maxState = null, maxCount = 0;
    for (const [st, c] of countsByState.entries()) {
      if (c > maxCount) { maxCount = c; maxState = st; }
    }
    const sorted = Array.from(countsByState.entries())
      .sort((a,b) => b[1] - a[1])
      .slice(0, 15);
    return {
      total: points.length,
      uniqueStates: countsByState.size,
      top: sorted,
      maxState,
      maxCount,
    };
  }, [countsByState, points]);

  // sizing: heat mode scales with per-state volume
  const radiusForPoint = (p) => {
    if (!heatSize) return 3;
    const count = countsByState.get(p.state || "UNKNOWN") || 1;
    // scale 2..10 by sqrt to reduce extreme variance
    const scaled = 2 + Math.min(10, Math.ceil(Math.sqrt(count)));
    return scaled;
  };

  return (
    <div className="w-full h-[calc(100vh-72px)] bg-[#f8fafc] p-4 md:p-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-base md:text-lg font-semibold text-[#0f172a]">
          Completed Orders Coverage
          <span className="ml-2 text-xs font-medium text-[#64748b]">
            ({loading ? "loading…" : `${summary.total} orders • ${summary.uniqueStates} states`})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#0f172a] flex items-center gap-2 bg-white border border-[#e5e7eb] rounded-lg px-3 py-1.5 shadow-sm">
            <input
              type="checkbox"
              checked={heatSize}
              onChange={(e) => setHeatSize(e.target.checked)}
            />
            Heat size
          </label>
        </div>
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-[#e5e7eb] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
        <MapContainer
          style={{ height: "78vh", width: "100%" }}
          center={[39.8283, -98.5795]} // continental US center-ish
          zoom={4}
          minZoom={3}
          maxZoom={16}
          scrollWheelZoom
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* Summary overlay */}
          <div className="absolute top-3 left-3 mt-40 z-[600]">
            <div className="bg-white/90 backdrop-blur rounded-xl border border-[#e5e7eb] shadow p-3 w-[260px]">
              <div className="text-xs font-semibold text-[#0f172a] mb-1">States by Volume</div>
              {summary.top.length === 0 && (
                <div className="text-xs text-[#64748b]">No completed orders yet.</div>
              )}
              <ul className="space-y-1.5">
                {summary.top.map(([st, c]) => (
                  <li key={st} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: colorForState(st) }}
                        title={st}
                      />
                      <span className="text-[#0f172a]">{st === "UNKNOWN" ? "Unknown" : st}</span>
                    </span>
                    <span className="text-[#334155] font-medium">{c}</span>
                  </li>
                ))}
              </ul>
              {summary.maxState && (
                <div className="mt-2 text-[11px] text-[#475569]">
                  Highest volume: <b>{summary.maxState}</b> ({summary.maxCount})
                </div>
              )}
            </div>
          </div>

          {/* Fit control */}
          <FitAllControl points={points} />

          {/* Dots */}
          {points.map((p) => {
            const color = colorForState(p.state || "UNKNOWN");
            return (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lng]}
                radius={radiusForPoint(p)}
                pathOptions={{ color, weight: 0, fillOpacity: 0.35, fillColor: color }}
              >
                <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                  <div className="text-[11px]">
                    <div><b>{p.state || "Unknown state"}</b></div>
                    <div className="text-[#475569]">{p.label}</div>
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
